import { Button, Field, Input, Text } from "@fluentui/react-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "#root/routes/constants";
import { useCreateUser, useBatchCreateUsers } from "#root/queries/users";
import { useState } from "react";
import { showToast } from "#root/components/GlobalToaster";
import { ROLES } from "#root/utils/roles";
import { toSentenceCase } from "#root/services/utils";
import "./styles.scss";
import ButtonGroup from "#root/components/ButtonGroup";
import EmailTagInput from "#root/components/EmailTagInput";
import { useCurrentTenant } from "#root/queries/local";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { VALIDATION_MESSAGES, VALIDATION_PATTERNS } from "#root/utils/validation";
import { TENANT_TYPE } from "#root/utils/tenants";
import { useTenantById } from "#root/queries/tenants";

interface UserForm{
    role: string;
    password: string;
}

interface SingleUserForm extends UserForm {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    
}

interface MultipleUserForm extends UserForm {
    genericAddress: string;
    numberOfUsers: string;
    emails: string[];
}

const initialSingleUserForm: SingleUserForm = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: '',
    password: ''
};

const initialMultipleUserForm: MultipleUserForm = {
    genericAddress: '',
    numberOfUsers: '',
    emails: [],
    role: '',
    password: ''
};

const AddUser: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [passwordManual, setPasswordManual] = useState(true);
    const [emailManual, setEmailManual] = useState(true);
    const isMultiple = searchParams.get('multiple') === 'true';

    // Single user form state
    const [singleUser, setSingleUser] = useState<SingleUserForm>(initialSingleUserForm);

    // Multiple users state
    const [multipleUser, setMultipleUser] = useState<MultipleUserForm>(initialMultipleUserForm);

    const createUserMutation = useCreateUser();
    const batchCreateUsersMutation = useBatchCreateUsers();

    const currentTenant = useCurrentTenant();
    const tenantId = currentTenant?.data;

    const { data: tenantResponse } = useTenantById({ id: tenantId || '' });
    const currentTenantItem = tenantResponse?.item;
    const tenantType = currentTenantItem?.type || TENANT_TYPE.RAPID_REUNIFICATION;

    let availableRoles = Object.values(ROLES).filter(role => role !== ROLES.GLOBALADMIN);
    if (tenantType === TENANT_TYPE.MERRGE_211) {
        availableRoles = Object.values(availableRoles).filter(role => role !== ROLES.USER && role !== ROLES.VIEWER);
    }

    const handleEmailModeChange = (value: boolean) => {
        setEmailManual(value);
    };

    const handlePasswordModeChange = (value: boolean) => {
        setPasswordManual(value);
    };

    const handleRoleChange = (value: string | string[]) => {
        if (typeof value === 'string') {
            if (isMultiple) {
                setMultipleUser({ ...multipleUser, role: value });
            } else {
                setSingleUser({ ...singleUser, role: value });
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (isMultiple) {
                // Multiple user create
                if (emailManual) {
                    const emails = multipleUser.emails;
                    if (emails.length === 0) {
                        showToast('Please add at least one valid email address', 'error');
                        return;
                    }
                    await batchCreateUsersMutation.mutateAsync({
                        users: {
                            emails,
                            password: passwordManual ? multipleUser.password : undefined,
                            role: multipleUser.role
                        }
                    });
                } else {
                    if (!multipleUser.genericAddress || !multipleUser.numberOfUsers || parseInt(multipleUser.numberOfUsers) <= 0) {
                        showToast('Please fill in all required fields', 'error');
                        return;
                    }
                    await batchCreateUsersMutation.mutateAsync({
                        users: {
                            genericAddress: multipleUser.genericAddress,
                            numberOfUsers: parseInt(multipleUser.numberOfUsers),
                            password: passwordManual ? multipleUser.password : undefined,
                            role: multipleUser.role
                        }
                    });
                }
                showToast('Users created successfully', 'success');
                navigate(ROUTES.ORGANIZATION_SETTINGS.USERS_ADMINISTRATION);
            } else {
                await createUserMutation.mutateAsync({
                    user: {
                        firstName: singleUser.firstName.trim(),
                        lastName: singleUser.lastName.trim(),
                        email: singleUser.email.trim(),
                        phoneNumber: singleUser.phoneNumber.trim(),
                        role: singleUser.role,
                        password: passwordManual ? singleUser.password : undefined
                    }
                });

                showToast('User created successfully', 'success');
                navigate(ROUTES.ORGANIZATION_SETTINGS.USERS_ADMINISTRATION);
            }
        } catch (error) {
            showToast('Failed to create users', 'error');
        }
    };

    return (
        <div className="add-user">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs/>
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>{isMultiple ? 'Add Multiple Users' : 'Add User'} <div className="divider"></div></h2>
                </div>
            </div>

            <form autoComplete="off" onSubmit={handleSubmit} className="add-user-form">
                {isMultiple ? (
                    <>
                        <Field label="Email" required>
                            <ButtonGroup
                                name="email-mode"
                                options={[
                                    { label: 'Add Manually', value: true },
                                    { label: 'Generic Addresses', value: false }
                                ]}
                                onChange={handleEmailModeChange}
                                value={emailManual}
                                required
                            />
                        </Field>
                        {emailManual ? (
                            <>
                                <Field required>
                                    <Text>Add Email Addresses</Text>
                                    <EmailTagInput
                                        value={multipleUser.emails}
                                        onChange={(emails) => { setMultipleUser({ ...multipleUser, emails: emails})}}
                                        placeholder="e.g. user1@example.com, user2@example.com"
                                    />
                                </Field>
                            </>
                        ) : (
                            <>
                                <Field label="Generic Address" required>
                                    <div className="generic-address">
                                        <Input
                                            value={multipleUser.genericAddress}
                                            onChange={(e) => setMultipleUser({ ...multipleUser, genericAddress: e.target.value })}
                                            required
                                        />
                                        <span className="suffix">{`_${tenantId}_#@EMCPlatforms.com`}</span>
                                    </div>
                                </Field>
                                <Field label="Number of Users" required>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={multipleUser.numberOfUsers}
                                        onChange={(e) => setMultipleUser({ ...multipleUser, numberOfUsers: e.target.value })}
                                        required
                                    />
                                </Field>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <div className="field-two-columns">
                            <Field label="First Name">
                                <Input value={singleUser.firstName} onChange={(e) => setSingleUser({ ...singleUser, firstName: e.target.value })} />
                            </Field>
                            <Field label="Last Name">
                                <Input value={singleUser.lastName} onChange={(e) => setSingleUser({ ...singleUser, lastName: e.target.value })} />
                            </Field>
                        </div>
                        <Field label="Email" required>
                            <Input type="email" required value={singleUser.email} onChange={(e) => setSingleUser({ ...singleUser, email: e.target.value })} />
                        </Field>
                    </>
                )}

                <Field label="Password" required hint={passwordManual ? VALIDATION_MESSAGES.PASSWORD : ""}>
                    <ButtonGroup
                        name="password-mode"
                        options={[
                            { label: 'Manual', value: true },
                            { label: 'Random', value: false }
                        ]}
                        onChange={handlePasswordModeChange}
                        value={passwordManual}
                        required
                    />
                    {passwordManual && (
                        <Input className="password-input"
                            autoComplete="new-password"
                            name="new-password"
                            type="password"
                            value={isMultiple ? multipleUser.password : singleUser.password}
                            onChange={(e) => isMultiple ? setMultipleUser({ ...multipleUser, password: e.target.value }) : setSingleUser({ ...singleUser, password: e.target.value })}
                            pattern={VALIDATION_PATTERNS.PASSWORD}
                            title={VALIDATION_MESSAGES.PASSWORD}
                            required
                        />
                    )}
                </Field>

                {!isMultiple && (
                    <Field label="Phone Number">
                        <Input value={singleUser.phoneNumber} onChange={(e) => setSingleUser({ ...singleUser, phoneNumber: e.target.value })} />
                    </Field>
                )}

                <Field label="Role" required>
                    <ButtonGroup
                        name="role"
                        options={availableRoles.map(role => ({ label: toSentenceCase(role), value: role }))}
                        onChange={handleRoleChange}
                        value={isMultiple ? multipleUser.role : singleUser.role}
                        required
                    />
                </Field>

                <div className="form-actions">
                    <Button
                        type="submit"
                        appearance="primary"
                        size="large"
                        disabled={createUserMutation.isPending || batchCreateUsersMutation.isPending}
                    >
                        {createUserMutation.isPending || batchCreateUsersMutation.isPending ? 'Creating...' : `Create ${isMultiple ? 'Users' : 'User'}`}
                    </Button>
                    <Button appearance="secondary" size="large" onClick={() => navigate(ROUTES.ORGANIZATION_SETTINGS.USERS_ADMINISTRATION)}>
                        Cancel
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default AddUser;